
import { Union, Record } from "../../fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type, option_type, int32_type, record_type, string_type } from "../../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { concat, substring, trimEnd } from "../../fable_modules/fable-library-js.5.3.0/String.js";
import { max } from "../../fable_modules/fable-library-js.5.3.0/Double.js";
import { fromParts, equals } from "../../fable_modules/fable-library-js.5.3.0/Decimal.js";
import { ChallengeStatusModule_tryParse, MinAllowedGoal_format, UnitName_singular } from "../Challenge.js";
import { monthDay } from "../ZonedDate.js";
import { endDate } from "./HeroHelpers.js";
import { map, filter } from "../../fable_modules/fable-library-js.5.3.0/Option.js";
import { getPhotoUrl } from "../Photo.js";
import { CoverImage_altText, CoverImage_renditionDimensions } from "../CoverImage.js";
import { ofArray } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { IconName } from "../Icons.js";
import { isHighSurrogate } from "../../fable_modules/fable-library-js.5.3.0/Char.js";
import { escapeDataString } from "../../fable_modules/fable-library-js.5.3.0/Util.js";

export class ShareContent extends Record {
    constructor(Title, Text$) {
        super();
        this.Title = Title;
        this.Text = Text$;
    }
}

export function ShareContent_$reflection() {
    return record_type("Shared.ChallengeShare.ShareContent", [], ShareContent, () => [["Title", string_type], ["Text", string_type]]);
}

export class ShareImage extends Record {
    constructor(Url, Width, Height, Alt) {
        super();
        this.Url = Url;
        this.Width = Width;
        this.Height = Height;
        this.Alt = Alt;
    }
}

export function ShareImage_$reflection() {
    return record_type("Shared.ChallengeShare.ShareImage", [], ShareImage, () => [["Url", string_type], ["Width", option_type(int32_type)], ["Height", option_type(int32_type)], ["Alt", string_type]]);
}

export const maxTextLength = 200;

export const fallbackImagePath = "/assets/YouThon_share_1200x630.png";

export const fallbackImageWidth = 1200;

export const fallbackImageHeight = 630;

const ellipsis = "…";

function trimBase(baseUrl) {
    return trimEnd(baseUrl, "/");
}

/**
 * Sole producer of challenge share URLs.
 */
export function url(baseUrl, challenge) {
    return `${trimBase(baseUrl)}/challenges/${challenge.Id}`;
}

function verbs(theme) {
    const matchValue = theme.toLowerCase();
    switch (matchValue) {
        case "walking":
            return ["walking", "walked"];
        case "running":
            return ["running", "ran"];
        case "cycling":
            return ["cycling", "cycled"];
        case "swimming":
            return ["swimming", "swam"];
        case "reading":
            return ["reading", "read"];
        case "meditation":
            return ["meditating", "meditated"];
        case "exercise":
            return ["exercising", "exercised"];
        default:
            return ["taking on", "took on"];
    }
}

function capitalise(s) {
    if (s.length === 0) {
        return s;
    }
    else {
        return s[0].toUpperCase() + substring(s, 1);
    }
}

function shortenCharity(name, excess) {
    return substring(name, 0, max(0, (name.length - excess) - ellipsis.length)) + ellipsis;
}

function buildText(challenge, isAsk, charityName) {
    let n, n_1;
    const patternInput = verbs(challenge.Theme);
    const present = patternInput[0];
    const past = patternInput[1];
    let subject;
    const matchValue = challenge.HostFirstName;
    subject = ((matchValue == null) ? (isAsk ? capitalise(present) : capitalise(past)) : (isAsk ? ((n = matchValue, concat(n, " is ", present))) : ((n_1 = matchValue, concat(n_1, " ", past)))));
    const units = equals(challenge.TargetAmount, fromParts(1, 0, 0, false, 0)) ? UnitName_singular(challenge.Unit) : challenge.Unit;
    const charity = (charityName === "") ? "" : concat(" for ", charityName);
    const goal = MinAllowedGoal_format(challenge.TargetAmount);
    if (isAsk) {
        const by = monthDay("UTC", endDate(challenge));
        return `${subject} ${goal} ${units}${charity}. Pledge per ${UnitName_singular(challenge.Unit)} by ${by}.`;
    }
    else {
        return `${subject} ${goal} ${units}${charity}.`;
    }
}

function textFor(challenge, isAsk) {
    const charityName = challenge.Charity.Name.trim();
    const full = buildText(challenge, isAsk, charityName);
    const fitted = ((full.length <= maxTextLength) ? true : (charityName === "")) ? full : buildText(challenge, isAsk, shortenCharity(charityName, full.length - maxTextLength));
    if (fitted.length <= maxTextLength) {
        return fitted;
    }
    else {
        return substring(fitted, 0, maxTextLength - 1) + ellipsis;
    }
}

/**
 * None unless the challenge is publicly shareable (Published through Finalized).
 */
export function content(challenge) {
    const share = (isAsk) => (new ShareContent(concat(challenge.Title, " - You-thon"), textFor(challenge, isAsk)));
    const matchValue = ChallengeStatusModule_tryParse(challenge.Status);
    let matchResult;
    if (matchValue != null) {
        switch (matchValue.tag) {
            case 2:
            case 3:
            case 4: {
                matchResult = 0;
                break;
            }
            case 5:
            case 6: {
                matchResult = 1;
                break;
            }
            default:
                matchResult = 2;
        }
    }
    else {
        matchResult = 2;
    }
    switch (matchResult) {
        case 0:
            return share(true);
        case 1:
            return share(false);
        default:
            return undefined;
    }
}

export function image(baseUrl, challenge, latestPhotoKey) {
    const matchValue = filter((c) => (c.Hidden == null), challenge.CoverImage);
    if (matchValue == null) {
        if (latestPhotoKey == null) {
            return new ShareImage(trimBase(baseUrl) + fallbackImagePath, fallbackImageWidth, fallbackImageHeight, "You-thon: Motivation for good.");
        }
        else {
            const key = latestPhotoKey;
            return new ShareImage(trimBase(baseUrl) + getPhotoUrl(key), undefined, undefined, concat("Daily log photo from ", challenge.Title));
        }
    }
    else {
        const cover = matchValue;
        return new ShareImage(trimBase(baseUrl) + cover.Url, CoverImage_renditionDimensions.Width, CoverImage_renditionDimensions.Height, CoverImage_altText(challenge.Title));
    }
}

/**
 * Share data handed to native share and the fallback channels: ShareContent plus the share URL.
 */
export class SharePayload extends Record {
    constructor(Title, Text$, Url) {
        super();
        this.Title = Title;
        this.Text = Text$;
        this.Url = Url;
    }
}

export function SharePayload_$reflection() {
    return record_type("Shared.ChallengeShare.SharePayload", [], SharePayload, () => [["Title", string_type], ["Text", string_type], ["Url", string_type]]);
}

/**
 * None exactly when `content` is None (status not shareable) — the only visibility rule for Share.
 */
export function payload(baseUrl, challenge) {
    return map((c) => (new SharePayload(c.Title, c.Text, url(baseUrl, challenge))), content(challenge));
}

export class ShareChannel extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["CopyLink", "Sms", "WhatsApp", "Email", "Facebook", "Bluesky", "X", "LinkedIn"];
    }
}

export function ShareChannel_$reflection() {
    return union_type("Shared.ChallengeShare.ShareChannel", [], ShareChannel, () => [[], [], [], [], [], [], [], []]);
}

export class ChannelGroup extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Messaging", "Social"];
    }
}

export function ChannelGroup_$reflection() {
    return union_type("Shared.ChallengeShare.ChannelGroup", [], ChannelGroup, () => [[], []]);
}

export const channels = ofArray([new ShareChannel(0, []), new ShareChannel(1, []), new ShareChannel(2, []), new ShareChannel(3, []), new ShareChannel(4, []), new ShareChannel(5, []), new ShareChannel(6, []), new ShareChannel(7, [])]);

export function channelLabel(channel) {
    switch (channel.tag) {
        case 1:
            return "Text message";
        case 2:
            return "WhatsApp";
        case 3:
            return "Email";
        case 4:
            return "Facebook";
        case 5:
            return "Bluesky";
        case 6:
            return "X";
        case 7:
            return "LinkedIn";
        default:
            return "Copy link";
    }
}

export function channelGroup(channel) {
    switch (channel.tag) {
        case 4:
        case 5:
        case 6:
        case 7:
            return new ChannelGroup(1, []);
        default:
            return new ChannelGroup(0, []);
    }
}

export function channelIcon(channel) {
    switch (channel.tag) {
        case 1:
            return new IconName(70, []);
        case 2:
            return new IconName(71, []);
        case 3:
            return new IconName(72, []);
        case 4:
            return new IconName(61, []);
        case 5:
            return new IconName(62, []);
        case 6:
            return new IconName(59, []);
        case 7:
            return new IconName(60, []);
        default:
            return new IconName(68, []);
    }
}

/**
 * Truncate to at most maxLength chars, ending with an ellipsis; never splits a UTF-16 surrogate pair.
 */
export function truncateText(maxLength, text) {
    if (maxLength < 1) {
        return "";
    }
    else if (text.length <= maxLength) {
        return text;
    }
    else {
        const cut = (maxLength - ellipsis.length) | 0;
        const cut_1 = (((cut > 0) && isHighSurrogate(text[cut - 1])) ? (cut - 1) : cut) | 0;
        return substring(text, 0, cut_1).trimEnd() + ellipsis;
    }
}

const xTextBudget = (280 - 23) - 1;

const blueskyLimit = 300;

/**
 * Target href for a channel; None for CopyLink, which is handled by the clipboard.
 */
export function channelHref(channel, payload_1) {
    const enc = escapeDataString;
    const textAndUrl = (payload_1.Text + " ") + payload_1.Url;
    switch (channel.tag) {
        case 1:
            return "sms:?&body=" + enc(textAndUrl);
        case 2:
            return "https://wa.me/?text=" + enc(textAndUrl);
        case 3:
            return (("mailto:?subject=" + enc(payload_1.Title)) + "&body=") + enc((payload_1.Text + "\n\n") + payload_1.Url);
        case 4:
            return "https://www.facebook.com/sharer/sharer.php?u=" + enc(payload_1.Url);
        case 5:
            return "https://bsky.app/intent/compose?text=" + enc((truncateText((blueskyLimit - payload_1.Url.length) - 1, payload_1.Text) + " ") + payload_1.Url);
        case 6:
            return (("https://x.com/intent/tweet?text=" + enc(truncateText(xTextBudget, payload_1.Text))) + "&url=") + enc(payload_1.Url);
        case 7:
            return "https://www.linkedin.com/sharing/share-offsite/?url=" + enc(payload_1.Url);
        default:
            return undefined;
    }
}

//# sourceMappingURL=ChallengeShare.js.map
